import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

class CartDrawerComponent extends Component {
  #summaryThreshold = 0.5;

  get #themeDrawer() {
    return this.closest('theme-drawer');
  }

  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.removeEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );
  }

  #handleDrawerOpen = () => {
    this.#updateStickyState();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')
        ?.shadowRoot?.querySelector('#shopify-installments-cta');

      cta?.addEventListener(
        'click',
        () => this.#themeDrawer?.close(),
        { once: true }
      );
    });
  };

  #handleCartLinesUpdate = (event) => {
    const shouldNotify =
      this.hasAttribute('auto-open') &&
      event.action === 'add';

    const sourceModal =
      event.target instanceof Element
        ? event.target.closest('dialog:modal')
        : null;

    const sourceForm =
      event.target instanceof Element
        ? event.target.closest('product-form-component')
        : null;

    const fallbackVariantId =
      sourceForm?.querySelector('input[name="id"]')?.value;

    const addedLines = Array.from(event.lines || []).map((line) => ({
      id: String(line.merchandiseId).split('/').pop(),
      quantity: Number(line.quantity) || 1,
    }));

    if (!addedLines.length && fallbackVariantId) {
      addedLines.push({
        id: String(fallbackVariantId),
        quantity:
          Number(
            sourceForm?.querySelector('input[name="quantity"]')?.value
          ) || 1,
      });
    }

    event.promise
      ?.then(({ detail }) => {
        requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldNotify || detail?.didError) return;

        const items = detail?.items || [];

        const addedItems = addedLines
          .map((line) => {
            const item = items.find(
              (entry) => String(entry.variant_id) === line.id
            );

            return item
              ? { item, quantity: line.quantity }
              : null;
          })
          .filter(Boolean);

        const showConfirmation = () => {
          if (!addedItems.length) {
            this.#themeDrawer?.open();
            return;
          }

          gloryShowCartConfirmation(addedItems);
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener(
            'close',
            showConfirmation,
            { once: true }
          );
        } else {
          showConfirmation();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[cart-drawer] Event promise rejected:',
            error
          );
        }
      });
  };

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;

    if (!drawerHeight) return;

    dialog.setAttribute(
      'cart-summary-sticky',
      summaryHeight / drawerHeight > this.#summaryThreshold
        ? 'false'
        : 'true'
    );
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}

function gloryShowCartConfirmation(addedItems) {
  const previous = document.getElementById(
    'glory-cart-confirmation'
  );

  if (previous) {
    if (previous.open) previous.close();
    previous.remove();
  }

  let style = document.getElementById(
    'glory-cart-confirmation-style'
  );

  if (!style) {
    style = document.createElement('style');
    style.id = 'glory-cart-confirmation-style';
    document.head.appendChild(style);
  }

  style.textContent = `
    #glory-cart-confirmation {
      box-sizing: border-box;
      position: fixed;
      inset: 0;
      margin: auto;
      width: min(560px, calc(100% - 28px));
      max-width: calc(100% - 28px);
      height: fit-content !important;
      min-height: 0 !important;
      block-size: fit-content !important;
      min-block-size: 0 !important;
      max-height: calc(100dvh - 40px);
      max-block-size: calc(100dvh - 40px);
      padding: 0;
      border: 1px solid #e5e5e5;
      border-radius: 10px;
      background: #fff;
      color: #111;
      overflow-y: auto;
      box-shadow: 0 16px 60px rgb(0 0 0 / 18%);
      font-family: inherit;
    }

    #glory-cart-confirmation[open] {
      display: block;
    }

    #glory-cart-confirmation::backdrop {
      background: rgb(0 0 0 / 45%);
    }

    #glory-cart-confirmation .glory-confirm-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 14px 20px;
      border-bottom: 1px solid #eee;
    }

    #glory-cart-confirmation h2 {
      margin: 0;
      font-size: 19px;
      line-height: 1.3;
      color: #111;
    }

    #glory-cart-confirmation .glory-confirm-close {
      display: grid;
      place-items: center;
      flex-shrink: 0;
      width: 44px;
      height: 44px;
      padding: 0;
      border: 0;
      background: transparent;
      color: #111;
      font-size: 30px;
      cursor: pointer;
    }

    #glory-cart-confirmation .glory-confirm-item {
      display: grid;
      grid-template-columns: 90px minmax(0, 1fr);
      gap: 16px;
      padding: 20px;
    }

    #glory-cart-confirmation .glory-confirm-image {
      display: block;
      width: 90px;
      height: 120px;
      object-fit: cover;
      border-radius: 4px;
    }

    #glory-cart-confirmation .glory-confirm-name {
      margin: 0 0 10px;
      font-size: 15px;
      font-weight: 500;
      line-height: 1.4;
      overflow-wrap: anywhere;
    }

    #glory-cart-confirmation .glory-confirm-meta {
      margin: 5px 0;
      font-size: 13px;
      line-height: 1.5;
      color: #666;
    }

    #glory-cart-confirmation .glory-confirm-price {
      margin: 12px 0 0;
      font-size: 16px;
      font-weight: 600;
      color: #111;
    }

    #glory-cart-confirmation .glory-confirm-actions {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;
      padding: 16px 20px 20px;
      border-top: 1px solid #eee;
    }

    #glory-cart-confirmation .glory-confirm-action {
      box-sizing: border-box;
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 48px;
      padding: 12px;
      border: 1px solid #ddd;
      border-radius: 4px;
      background: #fff;
      color: #111;
      font: inherit;
      font-size: 13px;
      line-height: 1.3;
      text-align: center;
      text-decoration: none;
      cursor: pointer;
    }

    #glory-cart-confirmation .glory-confirm-checkout {
      background: #000;
      border-color: #000;
      color: #fff;
    }

    #glory-cart-confirmation button:focus-visible,
    #glory-cart-confirmation a:focus-visible {
      outline: 2px solid #111;
      outline-offset: 3px;
    }

    @media (max-width: 380px) {
      #glory-cart-confirmation .glory-confirm-actions {
        grid-template-columns: 1fr;
      }
    }
  `;

  const previouslyFocused = document.activeElement;

  const dialog = document.createElement('dialog');
  dialog.id = 'glory-cart-confirmation';
  dialog.setAttribute(
    'aria-labelledby',
    'glory-confirm-title'
  );

  const header = document.createElement('div');
  header.className = 'glory-confirm-header';

  const heading = document.createElement('h2');
  heading.id = 'glory-confirm-title';
  heading.textContent = 'Ürün sepete eklendi';

  const closeButton = document.createElement('button');
  closeButton.type = 'button';
  closeButton.className = 'glory-confirm-close';
  closeButton.setAttribute('aria-label', 'Kapat');
  closeButton.textContent = '×';
  closeButton.addEventListener(
    'click',
    () => dialog.close()
  );

  header.append(heading, closeButton);
  dialog.appendChild(header);

  const currency =
    window.Shopify?.currency?.active || 'TRY';

  const formatMoney = (cents) =>
    new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency,
    }).format(Number(cents) / 100);

  for (const { item, quantity } of addedItems) {
    const row = document.createElement('div');
    row.className = 'glory-confirm-item';

    const imageUrl =
      item.featured_image?.url || item.image;

    if (imageUrl) {
      const image = document.createElement('img');
      image.className = 'glory-confirm-image';
      image.src = imageUrl;
      image.alt =
        item.product_title || item.title || '';

      row.appendChild(image);
    } else {
      row.style.gridTemplateColumns = '1fr';
    }

    const info = document.createElement('div');

    const name = document.createElement('p');
    name.className = 'glory-confirm-name';
    name.textContent =
      item.product_title || item.title;

    info.appendChild(name);

    for (const option of item.options_with_values || []) {
      if (
        option.value === 'Default Title' ||
        option.name === 'Title'
      ) {
        continue;
      }

      const meta = document.createElement('p');
      meta.className = 'glory-confirm-meta';
      meta.textContent = option.name + ': ' + option.value;

      info.appendChild(meta);
    }

    const count = document.createElement('p');
    count.className = 'glory-confirm-meta';
    count.textContent = 'Eklenen adet: ' + quantity;

    info.appendChild(count);

    const price = document.createElement('p');
    price.className = 'glory-confirm-price';
    price.textContent = formatMoney(
      (item.final_price ?? item.price) * quantity
    );

    info.appendChild(price);
    row.appendChild(info);
    dialog.appendChild(row);
  }

  const actions = document.createElement('div');
  actions.className = 'glory-confirm-actions';

  const continueButton = document.createElement('button');
  continueButton.type = 'button';
  continueButton.className = 'glory-confirm-action';
  continueButton.textContent = 'Alışverişe devam et';
  continueButton.addEventListener(
    'click',
    () => dialog.close()
  );

  const checkout = document.createElement('a');
  checkout.className =
    'glory-confirm-action glory-confirm-checkout';
  checkout.textContent = 'Alışverişi tamamla';

  const root =
    window.Shopify?.routes?.root || '/';

  checkout.href = root + 'checkout';

  actions.append(continueButton, checkout);
  dialog.appendChild(actions);

  dialog.addEventListener('click', (event) => {
    if (event.target !== dialog) return;

    const bounds = dialog.getBoundingClientRect();

    if (
      event.clientX < bounds.left ||
      event.clientX > bounds.right ||
      event.clientY < bounds.top ||
      event.clientY > bounds.bottom
    ) {
      dialog.close();
    }
  });

  dialog.addEventListener(
    'close',
    () => {
      dialog.remove();

      if (
        previouslyFocused instanceof HTMLElement &&
        previouslyFocused.isConnected
      ) {
        previouslyFocused.focus({
          preventScroll: true,
        });
      }
    },
    { once: true }
  );

  document.body.appendChild(dialog);
  dialog.showModal();

  continueButton.focus({
    preventScroll: true,
  });
}